import dayjs from "dayjs";
import type { useTranslations } from "use-intl";
import {
  z,
  coerce,
  date,
  discriminatedUnion,
  nativeEnum,
  number,
  object,
} from "zod";

import { CabinClass, TripType } from "@/features/flights/types/types";
import { airportSchema, citySchema } from "@/utils/locations";

export const flightFormLocationSchema = discriminatedUnion("type", [
  airportSchema,
  citySchema,
]);

export const flightSearchFormInputSchema = object({
  tripType: nativeEnum(TripType),
  from: flightFormLocationSchema.nullable(),
  to: flightFormLocationSchema.nullable(),
  dates: object({
    departure: coerce.date(),
    return: coerce.date().optional(),
  }),
  adult: number().min(1).max(9),
  child: number().min(0).max(9),
  infantWithoutSeat: number().min(0).max(9),
  cabinClass: nativeEnum(CabinClass),
});
export type FlightSearchFormInputValues = z.infer<
  typeof flightSearchFormInputSchema
>;

export const getFlightSearchFormSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  object({
    dates: object({
      departure: date({
        required_error: t("flights.validation.dates.departure"),
      }).refine((date) => {
        if (!date) return true;
        return !dayjs(date).endOf("day").isBefore(dayjs(), "day");
      }, t("flights.validation.dates.minDeparture")),
      return: date().optional(),
    }),
    from: flightFormLocationSchema
      .nullable()
      .refine((value) => value !== null, t("flights.validation.from")),
    to: flightFormLocationSchema
      .nullable()
      .refine((value) => value !== null, t("flights.validation.to")),
    tripType: nativeEnum(TripType, {
      required_error: t("flights.validation.tripType"),
    }),
    cabinClass: nativeEnum(CabinClass, {
      required_error: t("flights.validation.cabinClass"),
    }),
    adult: number().min(1).max(9),
    child: number().min(0).max(9),
    infantWithoutSeat: number().min(0).max(9),
  }).superRefine((values, ctx) => {
    const { dates, tripType } = values;
    const { departure: departureDate, return: returnDate } = dates;

    if (tripType === TripType.ROUND_TRIP && !returnDate) {
      ctx.addIssue({
        code: "custom",
        path: ["dates", "return"],
        message: t("flights.validation.dates.returnRequired"),
      });
    }

    if (returnDate && departureDate && returnDate < departureDate) {
      ctx.addIssue({
        code: "custom",
        path: ["dates", "return"],
        message: t("flights.validation.dates.minReturn"),
      });
    }
  });

export type FlightSearchFormValues = z.infer<
  ReturnType<typeof getFlightSearchFormSchema>
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typeCheck: z.input<ReturnType<typeof getFlightSearchFormSchema>> =
  {} as FlightSearchFormInputValues;
